import React, { useEffect, useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import './App.css';
import FilterBar from './components/Careers/FilterBar';
import JobCard from './components/Careers/JobCard';
import JobDetailPage from './components/Careers/JobDetailPage';
import ApplyModal from './components/Careers/ApplyModal';
import { supabase } from './lib/supabaseClient';
import AdminPortal from './components/Careers/AdminPortal';
import Home from './components/Home';
import Header from './components/Header';
import ServiceAI from './components/Services/ServiceAI';
import ServiceSAP from './components/Services/ServiceSAP';
import ServiceCloudDevOps from './components/Services/ServiceCloudDevOps';
import ServiceCyberSecurity from './components/Services/ServiceCyberSecurity';
import ServiceDataAnalytics from './components/Services/ServiceDataAnalytics';
import ServiceProductDevelopment from './components/Services/ServiceProductDevelopment';
import SolutionBPO from './components/Solutions/SolutionBPO';
import SolutionIT from './components/Solutions/SolutionIT';
import SolutionContingent from './components/Solutions/SolutionContingent';
import SolutionFulltime from './components/Solutions/SolutionFulltime';
import SolutionDirect from './components/Solutions/SolutionDirect';
import SolutionContract from './components/Solutions/SolutionContract';
import Footer from './components/Footer';
import ServicesOverview from './components/ServicesOverview';
import SolutionsOverview from './components/SolutionsOverview';
import IndustriesOverview from './components/IndustriesOverview';
import IndustryDetail from './components/Industries/IndustryDetail';
import TechnologiesOverview from './components/TechnologiesOverview';
import TechnologyDetail from './components/Technologies/TechnologyDetail';
import AboutUs from './components/AboutUs';
import Teams from './components/About/Teams';
import CompanyOverview from './components/About/CompanyOverview';
import Milestones from './components/About/Milestones';
import ContactUs from './components/About/ContactUs';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import ScrollToTop from './components/ScrollToTop';
import ChatWidget from './components/BOT/ChatWidget';
import Seo from './components/Seo';


import { useAuth } from './hooks/useAuth';
import { useJobs } from './hooks/useJobs';
import { useJobFilters } from './hooks/useJobFilters';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Custom Hooks
  const { session } = useAuth(supabase);
  const { jobs, loading, handleDeleteJob, handleToggleJobStatus, onJobPosted } = useJobs(supabase, session);
  const { setSearchQuery, filters, setFilters, filteredJobs } = useJobFilters(jobs);

  // Determine activeTab based on current URL path
  const activeTab = location.pathname === '/' ? 'home' : 
                    location.pathname === '/careers' ? 'candidate' :
                    location.pathname === '/admin' ? 'admin' : 
                    location.pathname.startsWith('/services') ? 'services' : 
                    location.pathname.startsWith('/solutions') ? 'solutions' : 
                    location.pathname.startsWith('/industries') ? 'industries' : 
                    location.pathname.startsWith('/technologies') ? 'technologies' : 
                    location.pathname.startsWith('/about') ? 'about' : 
                    (location.pathname === '/terms' || location.pathname === '/terms-of-service' || location.pathname === '/privacy' || location.pathname === '/privacy-policy') ? 'legal' : 'home';

  // Navigation scroll target
  const [scrollTarget, setScrollTarget] = useState(null);

  // Admin / UI state
  const [editingJob, setEditingJob] = useState(null);
  const [adminView, setAdminView] = useState('all'); // 'all' or 'mine'
  const [applyingJob, setApplyingJob] = useState(null);

  // Support legacy hash-style legal links like "#/privacy-policy".
  useEffect(() => {
    if (location.hash && location.hash.startsWith('#/')) {
      navigate(location.hash.slice(1), { replace: true });
    }
  }, [location.hash, navigate]);

  // Handle Supabase invite/recovery callbacks globally, even if user lands on home first.
  useEffect(() => {
    if (!supabase) return;

    const url = new URL(window.location.href);
    const tokenHash = url.searchParams.get('token_hash');
    const type = url.searchParams.get('type');
    const code = url.searchParams.get('code');
    const hashParams = new URLSearchParams((url.hash || '').replace(/^#/, ''));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const hashType = hashParams.get('type');

    if (code) {
      supabase.auth.exchangeCodeForSession(code).then(({ error }) => {
        if (!error) navigate('/admin', { replace: true });
      });
    } else if (tokenHash && (type === 'invite' || type === 'recovery')) {
      supabase.auth.verifyOtp({ token_hash: tokenHash, type }).then(({ error }) => {
        if (!error) navigate('/admin', { replace: true });
      });
    } else if (accessToken && refreshToken && (hashType === 'invite' || hashType === 'recovery')) {
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }).then(({ error }) => {
        if (!error) navigate('/admin', { replace: true });
      });
    }
  }, [navigate]);

  const adminFilteredJobs = useMemo(() => {
    if (!Array.isArray(jobs)) return [];
    if (adminView === 'all') return jobs;
    if (!session?.user) return [];
    return jobs.filter(j => j.author_id === session.user.id);
  }, [jobs, adminView, session]);


  return (
    <div className="min-h-screen bg-white text-slate-800 font-sans selection:bg-blue-100 selection:text-blue-900">
      <ScrollToTop />
      <Header 
        activeTab={activeTab} 
        setActiveTab={(tab) => {
            if (tab === 'home') navigate('/');
            else if (tab === 'candidate') navigate('/careers');
            else if (tab === 'admin') navigate('/admin');
            else if (tab === 'services') navigate('/services');
            else if (tab === 'solutions') navigate('/solutions');
            else if (tab === 'industries') navigate('/industries');
            else if (tab === 'technologies') navigate('/technologies');
            else if (tab === 'about') navigate('/about-us');
            else if (tab.startsWith('service-')) navigate(tab.replace('service-', '/services/'));
            else if (tab.startsWith('solution-')) navigate(tab.replace('solution-', '/solutions/'));
            else if (tab.startsWith('industry-')) navigate(tab.replace('industry-', '/industries/'));
            else if (tab.startsWith('technology-')) navigate(tab.replace('technology-', '/technologies/'));
            else if (tab.startsWith('about-')) navigate(tab.replace('about-', '/about-us/'));
        }} 
        setScrollTarget={setScrollTarget} 
      />

      <Routes>
        <Route path="/" element={<Home scrollTarget={scrollTarget} setScrollTarget={setScrollTarget} />} />
        
        {/* Top Level Overviews */}
        <Route path="/services" element={<ServicesOverview />} />
        <Route path="/solutions" element={<SolutionsOverview />} />
        <Route path="/industries" element={<IndustriesOverview />} />
        <Route path="/industries/:slug" element={<IndustryDetail />} />
        <Route path="/technologies" element={<TechnologiesOverview />} />
        <Route path="/technologies/:slug" element={<TechnologyDetail />} />
        <Route path="/about-us" element={<AboutUs />} />
        <Route path="/about-us/teams" element={<Teams />} />
        <Route path="/about-us/company-overview" element={<CompanyOverview />} />
        <Route path="/about-us/milestones" element={<Milestones />} />
        <Route path="/about-us/contact-us" element={<ContactUs />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/terms-of-service" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacy-policy" element={<Privacy />} />

        {/* Deep Dive Subpages */}
        <Route path="/services/ai" element={<ServiceAI />} />
        <Route path="/services/sap" element={<ServiceSAP />} />
        <Route path="/services/cloud-devops" element={<ServiceCloudDevOps />} />
        <Route path="/services/cybersecurity" element={<ServiceCyberSecurity />} />
        <Route path="/services/data-analytics" element={<ServiceDataAnalytics />} />
        <Route path="/services/product-development" element={<ServiceProductDevelopment />} />
        <Route path="/solutions/bpo" element={<SolutionBPO />} />
        <Route path="/solutions/it" element={<SolutionIT />} />
        <Route path="/solutions/contingent" element={<SolutionContingent />} />
        <Route path="/solutions/fulltime" element={<SolutionFulltime />} />
        <Route path="/solutions/full-time" element={<SolutionFulltime />} />
        <Route path="/solutions/strategic-staffing" element={<SolutionContingent />} />
        <Route path="/solutions/direct" element={<SolutionDirect />} />
        <Route path="/solutions/contract" element={<SolutionContract />} />
        
        <Route path="/careers" element={
          <div className="pt-24 pb-24 bg-slate-50 min-h-screen">
            <Seo
              title="Careers"
              description="Join Libsys Inc. Open roles across AI/ML, SAP, cybersecurity, cloud, data engineering, full-stack development, and staffing. Apply to current openings today."
              path="/careers"
            />
            <div className="container mx-auto px-6">
              <div className="flex flex-col lg:flex-row gap-12 relative">
                <div className="w-full lg:w-1/4">
                  <div className="lg:sticky lg:top-32">
                    <FilterBar
                      activeFilter={filters.department} 
                      onFilterChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))} 
                      onSearch={setSearchQuery} 
                      onGeneralApply={() => setApplyingJob({
                        id: 'general',
                        title: 'Strategic Talent Pool',
                        description: 'Join our exclusive talent network for future strategic opportunities.',
                        department: 'General'
                      })}
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between mb-8 border-b border-slate-200 pb-6">
                     <h2 className="text-2xl font-black text-primary tracking-tight">Open Positions <span className="text-secondary ml-2">({filteredJobs.length})</span></h2>
                  </div>
                  <div className="space-y-6">
                    <AnimatePresence>
                      {loading ? (
                        <div className="py-20 flex justify-center"><div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
                      ) : filteredJobs.map(job => (
                        <JobCard
                          key={job.id || job.job_code || job.title}
                          job={job}
                          onApply={() => setApplyingJob(job)}
                          onViewDetails={() => navigate(`/careers/${job.id || job.job_code}`)}
                          isAdmin={false}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </div>
        } />

        <Route
          path="/careers/:jobId"
          element={<JobDetailPage jobs={jobs} loading={loading} onApply={(job) => setApplyingJob(job)} />}
        />

        <Route path="/admin" element={
          <div className="pt-32 pb-24 min-h-screen bg-slate-50">
            <div className="container mx-auto px-6 max-w-5xl">
              <AdminPortal 
                editingJob={editingJob} 
                onJobPosted={(newJob) => {
                  onJobPosted(newJob, editingJob);
                  setEditingJob(null);
                }}
              />
              {session && (
                <div className="mt-16 space-y-8">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 px-2">
                    <h2 className="text-2xl font-black text-primary">Manage Postings</h2>
                    
                    <div className="flex bg-slate-200/50 p-1 rounded-xl w-fit">
                      <button
                        onClick={() => setAdminView('all')}
                        className={`px-6 py-2 rounded-lg text-xs font-black transition-all ${adminView === 'all' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-primary'}`}
                      >
                        All Postings
                      </button>
                      <button
                        onClick={() => setAdminView('mine')}
                        className={`px-6 py-2 rounded-lg text-xs font-black transition-all ${adminView === 'mine' ? 'bg-white text-primary shadow-sm' : 'text-slate-500 hover:text-primary'}`}
                      >
                        My Jobs
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-6">
                    {adminFilteredJobs.length > 0 ? (
                      adminFilteredJobs.map(job => (
                        <JobCard key={job.id} job={job} isAdmin={true} onEdit={() => setEditingJob(job)} onDelete={() => handleDeleteJob(job.id)} onToggleStatus={handleToggleJobStatus} />
                      ))
                    ) : (
                      <div className="py-20 text-center bg-white rounded-3xl border border-dashed border-slate-200">
                        <p className="text-slate-400 font-bold">No jobs found in this view.</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        } />
      </Routes>

      <AnimatePresence>
        {applyingJob && (
          <ApplyModal job={applyingJob} onClose={() => setApplyingJob(null)} />
        )}
      </AnimatePresence>

      <ChatWidget />


      <Footer setActiveTab={(tab) => {
          if (tab === 'home') navigate('/');
          else if (tab === 'legal') navigate('/terms-of-service');
          else if (tab === 'candidate') navigate('/careers');
          else if (tab === 'admin') navigate('/admin');
          else if (tab === 'services') navigate('/services');
          else if (tab === 'solutions') navigate('/solutions');
          else if (tab === 'industries') navigate('/industries');
          else if (tab === 'technologies') navigate('/technologies');
          else if (tab === 'about') navigate('/about-us');
      }} />
    </div>
  );
}

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppContent />
    </Router>
  );
}

export default App;
